import {useRecoilState, useResetRecoilState, useSetRecoilState} from "recoil";
import {useEffect} from "react";
import spaceState from "../state/spaceState";
import useFetchTiming from "../listing/hooks/useFetchTiming";
import {useParams} from "react-router-dom";
import firebaseApp from "../services/firebase";
import queryClient from "../services/queryClient";
import {useTranslation} from "react-i18next";
import firebaseState from "../state/firebaseState";

const useLoadSpace = (slug) => {
  const {i18n} = useTranslation();
  const {timing:timingSlug} = useParams();
  const timing = useFetchTiming(timingSlug);

  const [spaceAtom, setSpaceAtom] = useRecoilState(spaceState);
  const resetSpaceAtom = useResetRecoilState(spaceState);

  // Clear space on unmount
  useEffect(() => () => {
    resetSpaceAtom();
  }, []);

  useEffect(() => {
    if(!timing.data?.id) {
      return;
    }

    firebaseApp.analytics().setUserProperties({
      timing: timing.data?.id,
    });

    const roomRef = firebaseApp.database().ref(`room-metadata/${timingSlug}`)

    return () => {
      firebaseApp.analytics().setUserProperties({
        timing: null,
      });
    }
  }, [timing.data?.id]);

  useEffect(() => {
    i18n.changeLanguage(timing.data?.event?.locale?.id);
  }, [timing.data?.event?.locale?.id]);

  useEffect(() => {
    if(!timing.data) {
      return;
    }

    const now = +(new Date);
    const opens = +(new Date(timing.data.opens_at));
    const starts = +(new Date(timing.data.starts_at));
    const ends = +(new Date(timing.data.ends_at));
    const closes = +(new Date(timing.data.closes_at));
    const expires = +(new Date(timing.data.expires_at));

    const updateSpaceAtom = (now) => {
      console.log('DBG SPACE ATOM UPDATE', {
        isClosed: (closes && closes <= now) && (expires && expires <= now),
        isOpened: (!opens || opens <= now),
        isOpen: (!opens || opens <= now) && !(!closes || closes <= now),
        isStarted: (!starts || starts <= now),
        isEnded: (ends && ends <= now),
        isLive: (!starts || starts <= now) && !(!ends || ends <= now),
        isPublic: timing.data.is_public,
      })
      setSpaceAtom(old => ({
        ...old,
        id: timing.data.id,
        isClosed: (closes && closes <= now) || (expires && expires <= now),
        isOpened: (!opens || opens <= now),
        isOpen: (!opens || opens <= now) && !(!closes || closes <= now),
        isStarted: (!starts || starts <= now),
        isEnded: (ends && ends <= now),
        isLive: (!starts || starts <= now) && !(!ends || ends <= now),
        isPublic: timing.data.is_public,
      }))
    };

    updateSpaceAtom(now);

    const createUpdateInterval = (date, close = false) => {
      const diff = date - now;
      // max timeout value is 2147483647
      // lets just limit it to a day
      if(diff <= 0 || diff >= 86400000) {
        return null;
      }

      console.log('DBG SCHEDULE VIDEO REFRESH', Math.round(date - now) / 1000 / 60);

      return setTimeout(() => {
        updateSpaceAtom(+(new Date))
        if(close) {
          queryClient.invalidateQueries(['valid', timingSlug]);
        }
      }, diff);
    };

    // create timeouts to update space state for open/close/starts/ends date
    const timeoutHandlers = [
      createUpdateInterval(opens + 100),
      createUpdateInterval(starts + 100),
      createUpdateInterval(ends + 100),
      createUpdateInterval(closes + 100, true),
    ];
    if(expires > 0) {
      timeoutHandlers.push(createUpdateInterval(expires + 100, true));
    }

    return () => {
      timeoutHandlers.forEach((v) => v ? clearTimeout(v) : null);
    };
  }, [timing.data?.id, timing.data?.starts_at, timing.data?.ends_at, timing.data?.opens_at, timing.data?.closes_at]);

  timing.state = spaceAtom;

  return timing;
}

export default useLoadSpace;
